import React, { useEffect, useRef, useState } from "react";

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const fileFromBlob = (blob, name = "documento-recortado.jpg") => {
  return new File([blob], name, {
    type: blob.type || "image/jpeg",
    lastModified: Date.now(),
  });
};

const ModalDocumentoCropper = ({
  show,
  file,
  titulo = "Editor de documento",
  subtitulo = "Ajustá el área del documento para mejorar la lectura.",
  onCancel,
  onSave,
}) => {
  const stageRef = useRef(null);
  const imgRef = useRef(null);

  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imgReady, setImgReady] = useState(false);

  const [crop, setCrop] = useState({
    x: 12,
    y: 26,
    w: 76,
    h: 40,
  });

  const [drag, setDrag] = useState(null);

  useEffect(() => {
    if (!show || !file) return;

    const url = URL.createObjectURL(file);

    setImageFile(file);
    setImageUrl(url);
    setImgReady(false);
    setCrop({
      x: 12,
      y: 26,
      w: 76,
      h: 40,
    });

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [show, file]);

  const getStageRect = () => {
    if (!stageRef.current) return null;
    return stageRef.current.getBoundingClientRect();
  };

  const pointerToPercent = (e) => {
    const rect = getStageRect();
    if (!rect) return { x: 0, y: 0 };

    return {
      x: clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100),
      y: clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100),
    };
  };

  const startDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();

    const p = pointerToPercent(e);

    setDrag({
      type,
      startX: p.x,
      startY: p.y,
      cropInicial: crop,
    });
  };

  const resetCrop = () => {
    setCrop({
      x: 12,
      y: 26,
      w: 76,
      h: 40,
    });
  };

  const rotateImage = async (direction = "right") => {
    if (!imageUrl || !imageFile) return;

    const img = new Image();
    img.src = imageUrl;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;

    ctx.save();

    if (direction === "right") {
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
    } else {
      ctx.translate(0, canvas.height);
      ctx.rotate(-Math.PI / 2);
    }

    ctx.drawImage(img, 0, 0);
    ctx.restore();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );

    if (!blob) return;

    const rotatedFile = fileFromBlob(blob, "documento-rotado.jpg");
    const rotatedUrl = URL.createObjectURL(rotatedFile);

    if (imageUrl) URL.revokeObjectURL(imageUrl);

    setImageFile(rotatedFile);
    setImageUrl(rotatedUrl);
    setImgReady(false);
    resetCrop();
  };

  const guardarRecorte = async () => {
    if (!imgRef.current || !stageRef.current || !imageFile) return;

    const img = imgRef.current;
    const stageRect = stageRef.current.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    const cropXDisplay = (crop.x / 100) * stageRect.width;
    const cropYDisplay = (crop.y / 100) * stageRect.height;
    const cropWDisplay = (crop.w / 100) * stageRect.width;
    const cropHDisplay = (crop.h / 100) * stageRect.height;

    const offsetX = cropXDisplay - (imgRect.left - stageRect.left);
    const offsetY = cropYDisplay - (imgRect.top - stageRect.top);

    const scaleX = img.naturalWidth / imgRect.width;
    const scaleY = img.naturalHeight / imgRect.height;

    const sx = clamp(offsetX * scaleX, 0, img.naturalWidth);
    const sy = clamp(offsetY * scaleY, 0, img.naturalHeight);
    const sw = clamp(cropWDisplay * scaleX, 1, img.naturalWidth - sx);
    const sh = clamp(cropHDisplay * scaleY, 1, img.naturalHeight - sy);

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);

    const ctx = canvas.getContext("2d");

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.92)
    );

    if (!blob) {
      alert("No se pudo generar el recorte.");
      return;
    }

    const croppedFile = fileFromBlob(blob, "documento-recortado.jpg");
    const previewUrl = URL.createObjectURL(croppedFile);

    onSave?.({
      file: croppedFile,
      previewUrl,
    });
  };

  useEffect(() => {
    if (!drag) return;

    const handleMove = (e) => {
      const p = pointerToPercent(e);
      const dx = p.x - drag.startX;
      const dy = p.y - drag.startY;
      const c = drag.cropInicial;

      if (drag.type === "move") {
        setCrop({
          ...c,
          x: clamp(c.x + dx, 0, 100 - c.w),
          y: clamp(c.y + dy, 0, 100 - c.h),
        });
        return;
      }

      if (drag.type === "nw") {
        const nx = clamp(c.x + dx, 0, c.x + c.w - 10);
        const ny = clamp(c.y + dy, 0, c.y + c.h - 10);

        setCrop({
          x: nx,
          y: ny,
          w: c.w + (c.x - nx),
          h: c.h + (c.y - ny),
        });
        return;
      }

      if (drag.type === "ne") {
        const ny = clamp(c.y + dy, 0, c.y + c.h - 10);
        const nw = clamp(c.w + dx, 10, 100 - c.x);

        setCrop({
          x: c.x,
          y: ny,
          w: nw,
          h: c.h + (c.y - ny),
        });
        return;
      }

      if (drag.type === "sw") {
        const nx = clamp(c.x + dx, 0, c.x + c.w - 10);
        const nh = clamp(c.h + dy, 10, 100 - c.y);

        setCrop({
          x: nx,
          y: c.y,
          w: c.w + (c.x - nx),
          h: nh,
        });
        return;
      }

      if (drag.type === "se") {
        setCrop({
          x: c.x,
          y: c.y,
          w: clamp(c.w + dx, 10, 100 - c.x),
          h: clamp(c.h + dy, 10, 100 - c.y),
        });
      }
    };

    const handleUp = () => {
      setDrag(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [drag]);

  const cropStyle = {
    left: `${crop.x}%`,
    top: `${crop.y}%`,
    width: `${crop.w}%`,
    height: `${crop.h}%`,
  };

  if (!show) return null;

  return (
    <div style={overlayStyle}>
      <div style={headerStyle}>
        <div style={toolbarStyle}>
          <button
            type="button"
            style={iconButtonStyle}
            onClick={() => rotateImage("left")}
            title="Rotar izquierda"
          >
            ↺
          </button>

          <div
            style={{
              textAlign: "center",
              flex: "1 1 auto",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <div style={titleStyle}>{titulo}</div>
            <div style={subtitleStyle}>{subtitulo}</div>
          </div>

          <button
            type="button"
            style={iconButtonStyle}
            onClick={() => rotateImage("right")}
            title="Rotar derecha"
          >
            ↻
          </button>
        </div>
      </div>

      <div style={bodyStyle}>
        <div ref={stageRef} style={stageStyle}>
          {imageUrl ? (
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Documento"
              draggable={false}
              onLoad={() => setImgReady(true)}
              style={imageStyle}
            />
          ) : (
            <div style={{ color: "#fff", opacity: 0.7 }}>Sin imagen</div>
          )}

          {/* Área invisible para mover */}
          <div
            style={{
              ...cropMoveStyle,
              ...cropStyle,
            }}
            onPointerDown={(e) => startDrag(e, "move")}
          />

          {/* Línea superior */}
          <div
            style={{
              ...cropLineStyle,
              left: `${crop.x}%`,
              top: `${crop.y}%`,
              width: `${crop.w}%`,
              height: 2,
            }}
          />

          {/* Línea inferior */}
          <div
            style={{
              ...cropLineStyle,
              left: `${crop.x}%`,
              top: `${crop.y + crop.h}%`,
              width: `${crop.w}%`,
              height: 2,
            }}
          />

          {/* Línea izquierda */}
          <div
            style={{
              ...cropLineStyle,
              left: `${crop.x}%`,
              top: `${crop.y}%`,
              width: 2,
              height: `${crop.h}%`,
            }}
          />

          {/* Línea derecha */}
          <div
            style={{
              ...cropLineStyle,
              left: `${crop.x + crop.w}%`,
              top: `${crop.y}%`,
              width: 2,
              height: `${crop.h}%`,
            }}
          />

          <div
            style={{
              ...cropLabelStyle,
              left: `calc(${crop.x}% + 8px)`,
              top: `calc(${crop.y}% + 8px)`,
            }}
          >
            Área de lectura
          </div>

          <HandleAbs type="nw" crop={crop} onDown={startDrag} />
          <HandleAbs type="ne" crop={crop} onDown={startDrag} />
          <HandleAbs type="sw" crop={crop} onDown={startDrag} />
          <HandleAbs type="se" crop={crop} onDown={startDrag} />
        </div>
      </div>

      <div style={footerStyle}>
        <button type="button" style={cancelButtonStyle} onClick={onCancel}>
          Cancelar
        </button>

        <button type="button" style={resetButtonStyle} onClick={resetCrop}>
          Reset
        </button>

        <button
          type="button"
          style={{
            ...saveButtonStyle,
            opacity: imgReady ? 1 : 0.55,
            cursor: imgReady ? "pointer" : "not-allowed",
          }}
          disabled={!imgReady}
          onClick={guardarRecorte}
        >
          Guardar recorte
        </button>
      </div>
    </div>
  );
};

const HandleAbs = ({ type, crop, onDown }) => {
  const positions = {
    nw: {
      left: `calc(${crop.x}% - 8px)`,
      top: `calc(${crop.y}% - 8px)`,
      cursor: "nwse-resize",
    },
    ne: {
      left: `calc(${crop.x + crop.w}% - 8px)`,
      top: `calc(${crop.y}% - 8px)`,
      cursor: "nesw-resize",
    },
    sw: {
      left: `calc(${crop.x}% - 8px)`,
      top: `calc(${crop.y + crop.h}% - 8px)`,
      cursor: "nesw-resize",
    },
    se: {
      left: `calc(${crop.x + crop.w}% - 8px)`,
      top: `calc(${crop.y + crop.h}% - 8px)`,
      cursor: "nwse-resize",
    },
  };

  return (
    <span
      onPointerDown={(e) => onDown(e, type)}
      style={{
        ...handleAbsStyle,
        ...positions[type],
      }}
    />
  );
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  background: "rgba(0, 0, 0, 0.9)",
  display: "flex",
  flexDirection: "column",
  color: "#fff",
};

const headerStyle = {
  minHeight: 64,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderBottom: "1px solid rgba(255,255,255,.08)",
  padding: "6px 8px",
};

const toolbarStyle = {
  width: "min(96vw, 620px)",
  background: "#141923",
  border: "1px solid rgba(255,255,255,.1)",
  borderRadius: 18,
  padding: "7px 10px",
  display: "flex",
  gap: 8,
  alignItems: "center",
  boxShadow: "0 10px 35px rgba(0,0,0,.45)",
};

const iconButtonStyle = {
  width: 34,
  height: 34,
  minWidth: 34,
  borderRadius: "50%",
  border: "none",
  background: "#080a0f",
  color: "#fff",
  fontSize: 21,
  fontWeight: 800,
  cursor: "pointer",
};

const titleStyle = {
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  lineHeight: 1.15,
  whiteSpace: "normal",
};

const subtitleStyle = {
  fontSize: 10.5,
  opacity: 0.65,
  lineHeight: 1.2,
  marginTop: 3,
};

const bodyStyle = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 24,
};

const stageStyle = {
  position: "relative",
  width: "min(780px, 94vw)",
  height: "min(620px, calc(100vh - 190px))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  background: "#050608",
  borderRadius: 12,
  boxShadow:
    "0 0 0 1px rgba(255,255,255,.1), 0 24px 80px rgba(0,0,0,.7)",
};

const imageStyle = {
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  userSelect: "none",
  pointerEvents: "none",
  position: "relative",
  zIndex: 1,
};

const cropMoveStyle = {
  position: "absolute",
  background: "transparent",
  backgroundColor: "transparent",
  opacity: 0,
  cursor: "move",
  touchAction: "none",
  zIndex: 5,
};

const cropLineStyle = {
  position: "absolute",
  background: "#ff2b2b",
  boxShadow:
    "0 0 0 1px rgba(255,255,255,.65), 0 0 18px rgba(255,43,43,.28)",
  pointerEvents: "none",
  zIndex: 6,
};

const handleAbsStyle = {
  position: "absolute",
  width: 16,
  height: 16,
  borderRadius: "50%",
  background: "#ff2b2b",
  border: "2px solid #fff",
  boxShadow: "0 2px 10px rgba(0,0,0,.55)",
  touchAction: "none",
  zIndex: 8,
};

const cropLabelStyle = {
  position: "absolute",
  zIndex: 7,
  background: "rgba(0,0,0,.68)",
  border: "1px solid rgba(255,255,255,.18)",
  borderRadius: 8,
  padding: "4px 8px",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.5,
  pointerEvents: "none",
};

const footerStyle = {
  minHeight: 76,
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 8,
  padding: "10px 10px 14px",
  borderTop: "1px solid rgba(255,255,255,.08)",
  flexWrap: "wrap",
};

const cancelButtonStyle = {
  minWidth: 92,
  height: 40,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.18)",
  background: "transparent",
  color: "#fff",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.6,
};

const resetButtonStyle = {
  minWidth: 100,
  height: 40,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.18)",
  background: "#172033",
  color: "#fff",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: 0.6,
};

const saveButtonStyle = {
  minWidth: 145,
  height: 40,
  borderRadius: 10,
  border: "1px solid #60a5fa",
  background: "#0d6efd",
  color: "#fff",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 0.8,
};

export default ModalDocumentoCropper;